import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { Paged } from 'api/common/types/Page';
import styles from './ReminderCategoriesList.module.scss';
import InputSearch from 'common/components/inputSearch/InputSearch';
import { useDebouncedCallback } from 'use-debounce';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import { FaEllipsisH } from 'react-icons/fa';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import { ReminderCategoriesDto } from 'api/reminderCategories/models/ReminderCategoriesDto';
import { ReminderCategoriesSearchCriteria } from 'api/reminderCategories/models/ReminderCategoriesSearchCriteria';
import ReminderCategoriesService from 'api/reminderCategories/ReminderCategoriesService';
import { useHistory } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import AddIcon from 'assets/svg/desktop_add_new.svg';

type Props = {
};

const ReminderCategoriesList: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const isFirstLoad = useRef(true);
    const filterJsonKey = 'PARAMETERS_REMINDER_CATEGORIES_FILTER';

    const [reminderCategoriesPage, setReminderCategoriesPage] = useState<Paged<ReminderCategoriesDto>>();
    const [criteria, setCriteria] = useState<ReminderCategoriesSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'designation',
        orderBy: 'asc'
    });

    const history = useHistory();

    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<ReminderCategoriesDto | null>(null);

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasReminderCategoriesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_REMINDER_CATEGORIES_WRITE']);

    const showRemoveItemDialog = (item: ReminderCategoriesDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    };

    const renderBooleanCell = (row: ReminderCategoriesDto) => {
        return <>-</>;
    };

    const renderTableActionCell = (row: ReminderCategoriesDto) => {
        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem url={`/settings/reminderCategories/details/${row.id ?? ''}`}>
                            {t('common.details')}
                        </DropdownItem>
                        {hasReminderCategoriesWritePolicy && <DropdownItem url={`/settings/reminderCategories/edit/${row.id ?? ''}`} disabled={row.isSystem} disabled_message={t('reminder_categories.forbidden_tooltip')} >
                            {t('common.edit')}
                        </DropdownItem>}
                        {hasReminderCategoriesWritePolicy && <DropdownItem onClick={() => showRemoveItemDialog(row)} disabled={row.isSystem} disabled_message={t('reminder_categories.forbidden_tooltip')} >
                            {t('common.remove')}
                        </DropdownItem>}
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<ReminderCategoriesDto>[] = useMemo(() => {
        const cols: ListingTableColumn<ReminderCategoriesDto>[] = [
            {
                field: 'designation',
                name: t('reminder_categories.list.designation'),
                onSearch: onTableFilter,
                searchField: 'rc.designation'
            },
            {
                name: '',
                width: '60px',
                cellAlignment: 'right',
                preventClick: true,
                renderCell: renderTableActionCell,
                cellStyle: { overflow: 'unset' },
            },
        ];
        return cols;
    }, []);

    const getData = async () => {
        try {
            Loading.show();

            saveOrdernationFilters();

            const page = await ReminderCategoriesService.getList(criteria);
            if (page?.items?.length) {
                page.items.map(x => {
                    if (x.isSystem) {
                        x.designation = t('reminder_categories.category_types.' + x.designation as any);
                    }
                });
            }

            setReminderCategoriesPage(page);

            Loading.hide();
        } catch (error) {
            Logger.error(
                LOGGER_LOG_TYPE.REQUEST,
                `Couldn't get vehicle brands list`,
                error
            );
            addToast(t('common.messages.error_load_info'), {
                appearance: 'error',
            });
            Loading.hide();
        }
    };

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            await ReminderCategoriesService.remove(itemToRemove);
            setCriteria({ ...criteria, page: 1 });
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete damage type`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        setCriteria({ ...criteria, page: 1, allIn: value });
    }, 500);

    useEffect(() => {
        if (isFirstLoad.current == true) {
            loadFilters()
        } else {
            getData()
        }
    }, [criteria]);

    const saveOrdernationFilters = () => {
        saveFiltersOnCache(criteria);
    }

    const saveFiltersOnCache = (f: ReminderCategoriesSearchCriteria) => {
        localStorage.setItem(filterJsonKey, JSON.stringify(f));
    }

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(filterJsonKey);
        if (cachedFilters != null) { onChangeFilters(JSON.parse(cachedFilters), true) }
        else { getData() }

        isFirstLoad.current = false;
    }

    const onChangeFilters = (f: ReminderCategoriesSearchCriteria, isOnLoad?: boolean) => {
        saveFiltersOnCache(f);
        setCriteria(prev => ({
            ...prev,
            ...f,
            page: (isOnLoad && f.page) ? f.page : 1,
        }))
    }

    return (
        <div className='list'>
            <div className={styles.tabHeader}>
                <div>
                    <InputSearch
                        onChangeValue={debounced}
                        placeholder={t('reminder_categories.list.search_placeholder')}
                        defaultValue={criteria.allIn}
                    ></InputSearch>
                </div>
                <div>
                    {hasReminderCategoriesWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={() => history.push(`/settings/reminderCategories/new`)} />}
                </div>
            </div>
            {!isFirstLoad.current && <ListingTable
                columns={tableColumns}
                rows={reminderCategoriesPage?.items || []}
                onRowClick={row => history.push(`/settings/reminderCategories/details/${row.id ?? ''}`)}
                onHref={row => `/settings/reminderCategories/details/${row.id ?? ''}`}
                allowHover={true}
                initialSearch={{ colField: criteria.orderColumn ?? 'rc.designation', isOrderAsc: (criteria.orderBy && criteria.orderBy == 'asc') ? true : false }}
            />}
            <PaginationWithInfo
                itemName={t('reminder_categories.title')}
                currentPage={reminderCategoriesPage?.currentPage || 1}
                pageItems={reminderCategoriesPage?.items.length || 0}
                totalItems={reminderCategoriesPage?.totalItems || 0}
                onChange={page => setCriteria({ ...criteria, page })}
            />
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_damage_type', { type: itemToRemove?.designation ?? '' })} />

        </div>
    );
};

export default ReminderCategoriesList;
